import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import Navbar from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import { Upload, FileText, Send, Loader2, X, Mail, Paperclip, CheckCircle2, AlertTriangle, Trash2, Edit3, Download, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function Bulletins() {
    const { user } = useAuth();
    const isResponsable = user?.role === "responsable";
    const isAdmin = user?.role === "admin";
    const canView = isResponsable || isAdmin;

    const [alternants, setAlternants] = useState([]);
    const [bulletins, setBulletins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);

    const reload = async () => {
        try {
            const [aRes, bRes] = await Promise.all([
                api.get("/establishment/alternants"),
                api.get("/bulletins"),
            ]);
            setAlternants(aRes.data);
            setBulletins(bRes.data);
        } catch (e) { /* ignore */ }
        finally { setLoading(false); }
    };
    useEffect(() => { reload(); }, []);

    const removeBulletin = async (b) => {
        if (!window.confirm(`Supprimer définitivement le bulletin « ${b.alternant_name} » ? Cette action supprime aussi le message envoyé au tuteur et le PDF.`)) return;
        try {
            await api.delete(`/bulletins/${b.id}`);
            toast.success("Bulletin supprimé");
            reload();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
        }
    };

    const resendBulletin = async (b) => {
        if (!window.confirm(`Renvoyer le bulletin au tuteur (${b.tutor_email || "—"}) ?`)) return;
        try {
            await api.post(`/bulletins/${b.id}/resend`);
            toast.success("Bulletin renvoyé au tuteur");
            reload();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
        }
    };

    const exportBulletin = async (b) => {
        try {
            const { data } = await api.get(`/bulletins/${b.id}/export`);
            if (data.download_url) {
                window.open(`${window.location.origin}${data.download_url}`, "_blank");
            } else {
                toast.error("Aucun PDF attaché à ce bulletin.");
            }
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
        }
    };

    if (!canView) {
        return (
            <div className="min-h-screen bg-paper">
                <Navbar />
                <div className="max-w-2xl mx-auto px-6 py-16 text-center">
                    <h1 className="h-display text-2xl">Accès réservé au Responsable de formation</h1>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-paper">
            <Navbar />
            <main className="max-w-5xl mx-auto px-6 lg:px-10 py-12" data-testid="bulletins-page">
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-8">
                    <div>
                        <div className="label-eyebrow mb-2">Bulletins scolaires</div>
                        <h1 className="h-display text-4xl sm:text-5xl font-semibold leading-tight">
                            Envoyer un <span className="text-orange">bulletin</span> au tuteur
                        </h1>
                        <p className="mt-3 text-sm text-muted2 max-w-xl">
                            Importez le PDF du bulletin scolaire (ou un scan) et envoyez-le directement par e-mail au tuteur entreprise de l'alternant.
                        </p>
                    </div>
                    {isResponsable && (
                        <button onClick={() => setShowForm(true)} className="btn-primary !py-3 self-start sm:self-auto" data-testid="bulletins-new-button">
                            <Upload size={16} /> Nouveau bulletin
                        </button>
                    )}
                </div>

                {showForm && (
                    <BulletinForm
                        alternants={alternants}
                        onClose={() => setShowForm(false)}
                        onSaved={() => { setShowForm(false); reload(); }}
                    />
                )}

                <h2 className="h-display text-xl font-semibold mb-3">Historique ({bulletins.length})</h2>
                {loading ? (
                    <div className="panel p-10 text-center text-muted2 text-sm">Chargement…</div>
                ) : bulletins.length === 0 ? (
                    <div className="panel p-10 text-center" data-testid="bulletins-empty">
                        <FileText size={36} strokeWidth={1.5} className="text-muted2 mx-auto" />
                        <h3 className="h-display text-lg font-semibold mt-3">Aucun bulletin envoyé</h3>
                        <p className="text-sm text-muted2 mt-2">Cliquez sur « Nouveau bulletin » pour envoyer le premier.</p>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {bulletins.map((b) => (
                            <div key={b.id} className="panel p-4 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3" data-testid={`bulletin-row-${b.id}`}>
                                <div className="flex-1 min-w-0">
                                    <div className="font-semibold text-sm">{b.alternant_name}</div>
                                    <div className="text-xs text-muted2 mt-0.5">{b.period_label || "Période non spécifiée"}</div>
                                    <div className="text-xs font-mono text-muted2 mt-1.5 inline-flex items-center gap-1.5"><Mail size={11} /> {b.tutor_email}</div>
                                    {b.attachment_filename && (
                                        <div className="text-[11px] text-muted2 mt-0.5 inline-flex items-center gap-1.5"><Paperclip size={10} /> {b.attachment_filename}</div>
                                    )}
                                    {b.appreciation && <p className="text-[11px] text-muted2 mt-1.5 line-clamp-2 italic">{b.appreciation}</p>}
                                </div>
                                <div className="flex flex-col items-stretch sm:items-end gap-2 shrink-0">
                                    <div className="text-right">
                                        {b.sent_at ? (
                                            <span className="pill bg-emerald-50 text-emerald-700 border border-emerald-200 inline-flex items-center"><CheckCircle2 size={11} className="mr-1" /> Envoyé</span>
                                        ) : b.error ? (
                                            <span className="pill bg-amber-50 text-amber-700 border border-amber-200 inline-flex items-center"><AlertTriangle size={11} className="mr-1" /> En attente</span>
                                        ) : (
                                            <span className="pill bg-rule text-ink/60">—</span>
                                        )}
                                        <div className="text-[10px] font-mono text-muted2 mt-1">{new Date(b.created_at).toLocaleDateString("fr-FR")}</div>
                                    </div>
                                    {isResponsable && (
                                        <div className="flex flex-wrap gap-1 justify-end">
                                            {b.attachment_filename && (
                                                <button onClick={() => exportBulletin(b)} className="p-1.5 border border-rule hover:border-orange hover:text-orange transition-colors" title="Télécharger le PDF" data-testid={`bulletin-export-${b.id}`}>
                                                    <Download size={13} />
                                                </button>
                                            )}
                                            <button onClick={() => resendBulletin(b)} className="p-1.5 border border-rule hover:border-orange hover:text-orange transition-colors" title="Renvoyer au tuteur" data-testid={`bulletin-resend-${b.id}`}>
                                                <RefreshCw size={13} />
                                            </button>
                                            <button onClick={() => setEditing(b)} className="p-1.5 border border-rule hover:border-orange hover:text-orange transition-colors" title="Modifier le bulletin" data-testid={`bulletin-edit-${b.id}`}>
                                                <Edit3 size={13} />
                                            </button>
                                            <button onClick={() => removeBulletin(b)} className="p-1.5 border border-destructive/40 text-destructive hover:bg-destructive/10 transition-colors" title="Supprimer le bulletin" data-testid={`bulletin-delete-${b.id}`}>
                                                <Trash2 size={13} />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                {editing && (
                    <EditBulletinModal
                        bulletin={editing}
                        onClose={() => setEditing(null)}
                        onSaved={() => { setEditing(null); reload(); }}
                    />
                )}
            </main>
        </div>
    );
}

function BulletinForm({ alternants, onClose, onSaved }) {
    const [alternantId, setAlternantId] = useState(alternants[0]?.id || "");
    const [tutorEmail, setTutorEmail] = useState("");
    const [periodLabel, setPeriodLabel] = useState("");
    const [appreciation, setAppreciation] = useState("");
    const [file, setFile] = useState(null);
    const [uploadedPath, setUploadedPath] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    // Multi-recipient dispatch
    const [sendToAlternant, setSendToAlternant] = useState(false);
    const [sendToFormateurs, setSendToFormateurs] = useState(false);
    const [contacts, setContacts] = useState([]);

    useEffect(() => {
        api.get("/messages/contacts").then((r) => setContacts(r.data || [])).catch(() => setContacts([]));
    }, []);

    const handleFile = async (f) => {
        if (!f) return;
        if (f.size > 12 * 1024 * 1024) {
            toast.error("Fichier trop volumineux (12 Mo max)");
            return;
        }
        setFile(f);
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            const { data } = await api.post("/bulletins/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setUploadedPath(data.path);
            toast.success("Fichier importé");
        } catch (e) {
            setFile(null);
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Échec upload");
        } finally {
            setUploading(false);
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!alternantId) { toast.error("Sélectionnez un alternant"); return; }
        if (!tutorEmail.trim()) { toast.error("E-mail du tuteur requis"); return; }
        setSaving(true);
        try {
            const res = await api.post("/bulletins", {
                alternant_id: alternantId,
                tutor_email: tutorEmail.trim(),
                period_label: periodLabel,
                notes: [],
                appreciation_generale: appreciation,
                attachment_path: uploadedPath,
                attachment_filename: file?.name,
            });
            if (res.data.sent_at) {
                toast.success("Bulletin envoyé au tuteur ✓");
            } else {
                toast.success("Bulletin enregistré (e-mail pas encore configuré)");
            }
            // Additional recipients via internal messaging (alternant + formateurs)
            const extraRecipientIds = [];
            if (sendToAlternant) extraRecipientIds.push(alternantId);
            if (sendToFormateurs) {
                contacts.filter((c) => c.role === "formateur").forEach((f) => extraRecipientIds.push(f.id));
            }
            if (extraRecipientIds.length > 0) {
                const msgBody = `📎 Bulletin ${periodLabel ? `« ${periodLabel} » ` : ""}partagé par le responsable de formation.${appreciation ? `\n\n${appreciation}` : ""}`;
                await Promise.all(extraRecipientIds.map((uid) =>
                    api.post("/messages", {
                        to_user_id: uid,
                        body: msgBody,
                        attachment_path: uploadedPath || undefined,
                        attachment_filename: file?.name || undefined,
                        attachment_content_type: file?.type || undefined,
                    }).catch((err) => {
                        console.warn("extra recipient fail", uid, err);
                    })
                ));
                toast.success(`Bulletin partagé avec ${extraRecipientIds.length} destinataire(s) supplémentaire(s)`);
            }
            onSaved();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
        } finally {
            setSaving(false);
        }
    };

    return (
        <form onSubmit={submit} className="panel p-6 mb-8 border-2 border-orange/30" data-testid="bulletin-form">
            <div className="flex items-center justify-between mb-4">
                <h2 className="h-display text-xl font-semibold">Nouveau bulletin scolaire</h2>
                <button type="button" onClick={onClose} className="text-muted2 hover:text-ink p-1"><X size={16} /></button>
            </div>

            <div className="grid sm:grid-cols-2 gap-3 mb-4">
                <div>
                    <label className="label-eyebrow mb-2 block">Alternant *</label>
                    <select className="field-input" value={alternantId} onChange={(e) => setAlternantId(e.target.value)} required data-testid="bulletin-alternant-select">
                        <option value="">— Sélectionner —</option>
                        {alternants.map((a) => (
                            <option key={a.id} value={a.id}>{a.prenom} {a.nom} ({a.email})</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="label-eyebrow mb-2 block">E-mail du tuteur *</label>
                    <input type="email" className="field-input" value={tutorEmail} onChange={(e) => setTutorEmail(e.target.value)} placeholder="tuteur@entreprise.fr" required data-testid="bulletin-tutor-email-input" />
                </div>
                <div>
                    <label className="label-eyebrow mb-2 block">Période</label>
                    <input className="field-input" value={periodLabel} onChange={(e) => setPeriodLabel(e.target.value)} placeholder="Ex: Trimestre 2 — Mars 2026" data-testid="bulletin-period-input" />
                </div>
            </div>

            <div className="mb-4">
                <label className="label-eyebrow mb-2 block">Appréciation générale (optionnel)</label>
                <textarea
                    rows={3}
                    className="field-input resize-none"
                    value={appreciation}
                    onChange={(e) => setAppreciation(e.target.value)}
                    placeholder="Quelques mots qui accompagneront le bulletin dans l'e-mail au tuteur"
                    data-testid="bulletin-appreciation-input"
                />
            </div>

            <div className="mb-5">
                <label className="label-eyebrow mb-2 block">Bulletin (PDF ou scan)</label>
                <label htmlFor="bulletin-file-input" className="block border-2 border-dashed border-rule rounded-sm p-6 text-center cursor-pointer hover:border-orange hover:bg-orange/5 transition-colors">
                    {uploading ? (
                        <>
                            <Loader2 size={22} className="text-orange mx-auto mb-2 animate-spin" />
                            <div className="text-sm font-medium">Import en cours…</div>
                        </>
                    ) : file && uploadedPath ? (
                        <>
                            <CheckCircle2 size={22} className="text-emerald-500 mx-auto mb-2" />
                            <div className="text-sm font-medium">{file.name}</div>
                            <div className="text-xs text-muted2 mt-1">Cliquez pour changer de fichier</div>
                        </>
                    ) : (
                        <>
                            <Upload size={22} className="text-orange mx-auto mb-2" />
                            <div className="text-sm font-medium">Cliquez pour importer un PDF ou un scan</div>
                            <div className="text-xs text-muted2 mt-1">PDF, JPG, PNG · 12 Mo max</div>
                        </>
                    )}
                    <input
                        id="bulletin-file-input"
                        type="file"
                        accept="application/pdf,image/*"
                        className="hidden"
                        onChange={(e) => handleFile(e.target.files?.[0])}
                        data-testid="bulletin-file-input"
                    />
                </label>
            </div>

            {/* Multi-recipient dispatch */}
            <div className="mb-5 border border-rule p-4 bg-surface" data-testid="bulletin-recipients">
                <div className="label-eyebrow mb-2">Destinataires supplémentaires (interne)</div>
                <p className="text-[11px] text-muted2 mb-3">Le PDF part déjà par e-mail au tuteur. Vous pouvez aussi le partager dans la messagerie Profawz à l'alternant et/ou aux formateurs de votre établissement.</p>
                <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                            type="checkbox"
                            checked={sendToAlternant}
                            onChange={(e) => setSendToAlternant(e.target.checked)}
                            className="accent-orange"
                            data-testid="bulletin-send-alternant"
                        />
                        <span>📬 Envoyer aussi à l'alternant (messagerie Profawz)</span>
                    </label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                            type="checkbox"
                            checked={sendToFormateurs}
                            onChange={(e) => setSendToFormateurs(e.target.checked)}
                            className="accent-orange"
                            data-testid="bulletin-send-formateurs"
                        />
                        <span>👥 Envoyer à tous les formateurs de l'établissement ({contacts.filter((c) => c.role === "formateur").length})</span>
                    </label>
                </div>
            </div>

            <div className="flex justify-end gap-2">
                <button type="button" onClick={onClose} className="btn-outline">Annuler</button>
                <button type="submit" disabled={saving || uploading} className="btn-primary" data-testid="bulletin-submit-button">
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                    {saving ? "Envoi…" : "Envoyer au tuteur"}
                </button>
            </div>
        </form>
    );
}

function EditBulletinModal({ bulletin, onClose, onSaved }) {
    const [title, setTitle] = useState(bulletin.title || "");
    const [body, setBody] = useState(bulletin.body || bulletin.appreciation || "");
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.put(`/bulletins/${bulletin.id}`, { title, body });
            toast.success("Bulletin mis à jour");
            onSaved();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-ink/70 flex items-center justify-center p-4" onClick={onClose} data-testid="bulletin-edit-modal">
            <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-paper max-w-lg w-full border border-rule p-6 space-y-4">
                <div className="flex items-start justify-between">
                    <div>
                        <div className="label-eyebrow">Modification de bulletin</div>
                        <h2 className="h-display text-xl font-semibold mt-1">Bulletin de {bulletin.alternant_name}</h2>
                        <p className="text-[11px] text-muted2 mt-1">Note : seuls le titre et le texte sont modifiables. Pour changer le PDF, supprimez et recréez.</p>
                    </div>
                    <button type="button" onClick={onClose} className="text-muted2 hover:text-ink"><X size={16} /></button>
                </div>
                <div>
                    <label className="label-eyebrow mb-1.5 block">Titre (optionnel)</label>
                    <input className="field-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="ex : Bulletin S1 — 2025/2026" data-testid="bulletin-edit-title" />
                </div>
                <div>
                    <label className="label-eyebrow mb-1.5 block">Message / Appréciation</label>
                    <textarea rows={6} className="field-input resize-none text-sm" value={body} onChange={(e) => setBody(e.target.value)} maxLength={8000} data-testid="bulletin-edit-body" />
                </div>
                <div className="flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="btn-outline !text-xs">Annuler</button>
                    <button type="submit" disabled={saving} className="btn-primary !text-xs" data-testid="bulletin-edit-save">
                        {saving ? <Loader2 size={14} className="animate-spin" /> : "Enregistrer"}
                    </button>
                </div>
            </form>
        </div>
    );
}
